import { useMemo, useState } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from './popover.jsx';
import { cn } from '@/lib/utils.js';

// Dependency-free calendar date picker — a month grid inside the shared Radix
// Popover. Built without react-day-picker on purpose: no new build dependency
// to trip the Render NODE_ENV=production install (CLAUDE.md build rule), and the
// month grid is all this surface needs.
//
// The value is a bare YYYY-MM-DD string in/out, so callers' date plumbing is
// unchanged. All date math is LOCAL — never `new Date("YYYY-MM-DD")`, which
// parses as UTC midnight and renders a day early in US timezones.

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function parseYmd(ymd) {
  if (typeof ymd !== 'string') return null;
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]) };
}

function fmtYmd(y, mo, d) {
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function humanLabel(ymd) {
  const p = parseYmd(ymd);
  if (!p) return '';
  return new Date(p.y, p.mo - 1, p.d).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}

function monthTitle(y, mo) {
  return new Date(y, mo - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export function DatePicker({ value, onChange, placeholder = 'Select a date', invalid, disabled, className }) {
  const [open, setOpen] = useState(false);
  const selected = parseYmd(value);
  const today = new Date();
  const todayKey = fmtYmd(today.getFullYear(), today.getMonth() + 1, today.getDate());

  // Visible month. Seeded from the selected value, else today; re-seeded each
  // time the popover opens so an external prefill lands on the right month.
  const [view, setView] = useState(() =>
    selected ? { y: selected.y, mo: selected.mo } : { y: today.getFullYear(), mo: today.getMonth() + 1 }
  );

  const cells = useMemo(() => {
    const startDow = new Date(view.y, view.mo - 1, 1).getDay();
    const daysInMonth = new Date(view.y, view.mo, 0).getDate();
    const out = [];
    for (let i = 0; i < startDow; i++) out.push(null);
    for (let d = 1; d <= daysInMonth; d++) out.push(d);
    return out;
  }, [view.y, view.mo]);

  function shiftMonth(delta) {
    setView((v) => {
      const idx = (v.y * 12 + (v.mo - 1)) + delta;
      return { y: Math.floor(idx / 12), mo: (idx % 12) + 1 };
    });
  }

  function pick(d) {
    onChange?.(fmtYmd(view.y, view.mo, d));
    setOpen(false);
  }

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        if (o) {
          const p = parseYmd(value);
          if (p) setView({ y: p.y, mo: p.mo });
        }
        setOpen(o);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex min-h-[44px] w-full items-center gap-2 rounded-card border border-border bg-bg px-3 py-2 text-left text-sm',
            'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 focus:ring-offset-bg',
            'disabled:cursor-not-allowed disabled:opacity-50',
            invalid && 'border-warning ring-1 ring-warning/40',
            className
          )}
        >
          <CalendarIcon size={15} strokeWidth={1.5} className="shrink-0 text-text-dim" />
          <span className={cn('flex-1 truncate', value ? 'text-text' : 'text-text-dim')}>
            {value ? humanLabel(value) : placeholder}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => shiftMonth(-1)}
            aria-label="Previous month"
            className="grid h-7 w-7 place-items-center rounded text-text-muted hover:bg-surface-strong hover:text-text"
          >
            <ChevronLeft size={16} strokeWidth={1.5} />
          </button>
          <div className="text-sm font-medium text-text">{monthTitle(view.y, view.mo)}</div>
          <button
            type="button"
            onClick={() => shiftMonth(1)}
            aria-label="Next month"
            className="grid h-7 w-7 place-items-center rounded text-text-muted hover:bg-surface-strong hover:text-text"
          >
            <ChevronRight size={16} strokeWidth={1.5} />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-center">
          {WEEKDAYS.map((w) => (
            <div key={w} className="py-1 text-[10px] font-semibold uppercase tracking-wide text-text-dim">{w}</div>
          ))}
          {cells.map((d, i) => {
            if (d == null) return <div key={`b${i}`} />;
            const key = fmtYmd(view.y, view.mo, d);
            const isSelected = key === value;
            const isToday = key === todayKey;
            return (
              <button
                key={key}
                type="button"
                onClick={() => pick(d)}
                className={cn(
                  'grid h-9 w-9 place-items-center rounded-card text-sm',
                  isSelected
                    ? 'bg-accent font-semibold text-white'
                    : 'text-text hover:bg-surface-strong',
                  !isSelected && isToday && 'ring-1 ring-accent/50'
                )}
              >
                {d}
              </button>
            );
          })}
        </div>
        <div className="mt-2 flex justify-end border-t border-border pt-2">
          <button
            type="button"
            onClick={() => { onChange?.(todayKey); setOpen(false); }}
            className="rounded-card px-2 py-1 text-[11px] font-medium text-accent hover:bg-accent-soft"
          >
            Today
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
